import type { OrderModification } from "@utils/func/orderModifications";

export interface AccomplishedOrderResponse {
  status: number;
  message: string;
  data: {
    orderList: Order[];
    rowsCount: number;
  };
  RF: string;
}

export interface UnaccomplishedOrderResponse {
  status: number;
  message: string;
  data: {
    orderList: Order[];
    rowsCount: number;
  };
  RF: string;
}

export enum OrderListStatusEnum {
  pending = "pending",
  waiting_for_pickup = "waiting_for_pickup",
  delivering = "delivering",
  completed = "completed",
  canceled = "canceled",
}
export interface Owner {
  owner_type: string;
  label: string;
}

export interface Supplier {
  supplier_id: string;
  supplier_name: string;
}

export interface ItemSummary {
  pn: string;
  qty: number;
}

export interface Order {
  platform_order_id: string;
  supplier_id: string;
  /** 該訂單所屬瓦斯行（詳情 API 回傳；標頭顯示用，直接開網址／重整也有） */
  supplier?: {
    supplier_id: string;
    supplier_name: string | null;
  };
  owner_type: string;
  owner_id: number;
  placed_by_customer_id: number;
  payer_customer_id: number;
  order_status: string;
  client_request_id: string;
  customer_address_id: number;
  address_snapshot_json: AddressSnapshot;
  payer_snapshot_json: PayerSnapshot;
  auth_snapshot_json: AuthSnapshot | null;
  supplier_order_snapshot_json: SupplierOrderSnapshot;
  mirror_sync_version: number;
  mirror_last_synced_at: string;
  create_time_stamp: string;
  update_time_stamp: string;
  /** 該訂單所屬 supplier 的 system_setting.hide_amount_on_pending；待確認且為 true 時前端隱藏金額。 */
  hide_amount_on_pending?: boolean;
  /**
   * 金錢退款進度（app-api 直接 join refund_order，頂層欄位）。
   * 舊版／練習站可能沒有；勿再從 snapshot.refund_orders 讀。
   */
  refund_orders?: RefundOrders;
  /**
   * 訂單被修改過的摘要（app-api 由 order_record 轉成客戶可見版本）。
   * 舊版 API／練習站沒有這個欄位；一律經 hasModifications() 判斷。
   */
  modifications?: OrderModification[];
}

export interface AddressSnapshot {
  city: string;
  road: string;
  room: string | null;
  floor: string | null;
  site_id: string;
  latitude: number;
  longitude: number;
  full_address: string;
  address_number: string;
  customer_address_id: number;
}

export interface PayerSnapshot {
  label: string;
  amount: number;
  requires_auth: boolean;
  payment_method: string;
  platform_service_fee: number;
}

export interface AuthSnapshot {
  // populate when auth_snapshot_json is not null
  [key: string]: unknown;
}

// 對齊 lpg-api-service/src/service/buildSupplierOrderSnapshot.ts（snapshot spec v0.6.0）
export interface SupplierOrderSnapshot {
  items: OrderItem[];
  payups: OrderPayup[];
  /** 退氣／存氣（公斤數，有參與訂單金額計算）——與訂單詳情頂層 refund_orders 是兩回事 */
  refunds: OrderRefund[];
  /**
   * @deprecated 金錢退款改由訂單詳情頂層 `Order.refund_orders`（join refund_order）。
   * 舊 snapshot 可能仍帶此欄，前端勿再依賴。
   */
  refund_orders?: RefundOrders;
  invoices: OrderInvoice[];
  checklist: OrderChecklist | null;
  cylinders: OrderCylinder[];
  mortgages: OrderMortgage[];
  order_list: OrderList;
  usage_fees: OrderUsageFee[];
  sync_version: number;
  last_synced_at: string;
  cylinder_actions: OrderCylinderAction[];
}

/** 金錢退款單狀態（對齊 refund_order.status） */
export type RefundOrderStatus =
  | "bank"
  | "processing"
  | "manual"
  | "failed"
  | "done"
  | "cancel";

export type RefundOrderMethod = "electronic" | "cash";

export interface RefundOrderEvent {
  event_type: "created" | "bank_ok" | "bank_fail" | "manual" | "done" | "cancel" | "note";
  created_at: string;
}

export interface RefundOrderRecord {
  refund_id: string;
  status: RefundOrderStatus;
  refund_method: RefundOrderMethod;
  refund_amount: number;
  actual_refund: number;
  service_fee: number;
  refund_fee: number;
  reason: string;
  created_at: string;
  completed_at: string | null;
  events: RefundOrderEvent[];
}

/**
 * 一張訂單的所有金錢退款。累計金額由後端算好帶下來——
 * 前端自己加總會漏掉 cancel 的那幾筆，而且金額判斷分散兩邊遲早不一致。
 */
export interface RefundOrders {
  records: RefundOrderRecord[];
  /** 已退合計（只計 done） */
  refunded_total: number;
  /** 進行中金額合計 */
  pending_total: number;
  refundable_remaining: number;
  state: "none" | "running" | "done";
}

/** 訂單品項：瓦斯 與 商品（其他訂單品項）以 kind 區分 */
export type OrderItem = OrderGasItem | OrderCommodityItem;

export interface OrderGasItem {
  kind: "gas";
  order_gas_id: number;
  gas_id: number | null;
  gas_type: string | null;
  gas_kilogram: number | null;
  pricing_type: "gas" | "cis_gas";
  gp_id: number | null;
  cis_gp_id: number | null;
  unit_price: number;
  numbers_of_cylinder: number;
  delivery_id: number | null;
  delivery_location: string | null;
  customer_delivery_id: number | null;
  install_location: {
    customer_delivery_id: number;
    usage_location: string | null;
    note: string | null;
    floor: number | null;
    is_elevator: boolean | null;
  } | null;
  replacing_customer_delivery_id: number | null;
  replacing_spec: number | null;
  subtotal: number;
}

export interface OrderCommodityItem {
  kind: "commodity";
  order_commodity_id: number;
  commodity_id: number;
  commodity_name: string | null;
  commodity_type: string | null;
  commodity_price_id: number;
  unit_price: number;
  numbers_of_commodity: number;
  delivery_id: number;
  delivery_location: string | null;
  subtotal: number;
}

/** 使用費（瓦斯行依安裝位置使用天數收取；money = 加總金額） */
export interface OrderUsageFee {
  order_usage_fee_id: number;
  number_of_records: number;
  money: number;
  create_time_stamp: string | null;
}

/** 押退桶（cis_cylinder_mortgage）。take_cylinder_type: 押桶 / 借桶 / 還桶 / 寄桶。subtotal = money × numbers */
export interface OrderMortgage {
  cis_cylinder_mortgage_id: number;
  gas_id: number | null;
  take_cylinder_type: string | null;
  cylinder_specification: number | null;
  money: number;
  numbers_of_cylinder: number;
  subtotal: number;
}

/** 退氣 / 存氣。order_refund_type: '退氣' = 退現金、'存氣' = 下次折抵。amount = refund_gas_kilogram × gas_price */
export interface OrderRefund {
  order_refund_id: number;
  order_refund_type: string;
  refund_gas_type: string | null;
  refund_gas_kilogram: number;
  gas_price: number;
  amount: number;
}

export interface OrderPayup {
  order_payup_id: number;
  amount: number;
  payment_method: string | null;
  paid_at: string | null;
  payup_work_id: number | null;
}

export interface OrderInvoice {
  invoice_order_id: number;
  invoice_number: string | null;
  carrier: string | null;
  tax_id: string | null;
  issued_at: string | null;
  voided_at: string | null;
}

/** 鋼瓶買賣（order_cylinder × cylinder_price）。customer_action_type: 客戶購入(+) / 客戶賣出(−) */
export interface OrderCylinder {
  order_cylinder_id: number;
  cp_id: number | null;
  customer_action_type: string | null;
  cylinder_specification: number | null;
  price: number;
  numbers_of_cylinder: number;
  subtotal: number;
}

export interface OrderCylinderAction {
  cylinder_action_id: number;
  action_type: string | null;
  barcode: string | null;
  happened_at: string | null;
}

export interface OrderChecklist {
  customer_check_list_id: number;
  items: Record<string, unknown>;
  checked_at: string | null;
}

export interface OrderList {
  note: string | null;
  cis_id: string;
  discount: number;
  order_id: string;
  address_id: number;
  courier_id: number | null;
  courier_name: string | null;
  gas_discount: number;
  order_status: string;
  contact_phone: string;
  customer_note: string | null;
  delivery_type: string;
  tax_id_number: string | null;
  arrears_cached: number;
  customer_phone: string;
  end_time_stamp: string | null;
  app_customer_note: string | null;
  total_price_cached: number;
  /** 客戶實付（不含樓層費的單＝訂單金額＋樓層費；其餘＝訂單金額）。7.102 以前的舊快照沒有 */
  order_amount_cached?: number | null;
  /** 訂單金額不含樓層費（樓層費開關）。7.102 以前的舊快照沒有，視為 false */
  floor_fee_excluded?: boolean;
  customer_address_id: number;
  delivery_time_stamp: string;
  order_delivery_status: string;
  estimated_delivery_time: string | null;
  /**
   * 預約配送的完整時段字串（例「09:00–12:00」）。
   * 待後端 snapshot 接上配送時段 module（join weekly_slot/special_slot）後帶入；目前可能為 undefined。
   */
  delivery_slot_label?: string | null;
  /** 樓層費總額（SUM order_gas.floor_fee × numbers）；total 內以 + 計入。後端 7.7.0+ 帶入 */
  floor_fee?: number;
  /** 樓層費折抵；total 內以 − 計入 */
  floor_fee_discount_amount?: number;
  /** 優惠券折抵客戶金額（顯示用） */
  coupon_discount_amount?: number;
  /** 優惠券由 supplier 吸收的折抵（total 內以 − 計入；對帳用） */
  coupon_supplier_discount_amount?: number;
  /** 優惠券名稱快照 */
  coupon_name?: string | null;
}

export interface OrderListItem {
  platform_order_id: string;
  display_id: string;
  status: OrderListStatusEnum;
  total: number;
  created_at: string;
  owner?: {
    label: string;
  };
  /** 該訂單所屬瓦斯行（列表 API 回傳；詳情 API 不含，故由列表帶入詳情頁標頭） */
  supplier?: {
    supplier_id: string;
    supplier_name: string | null;
  };
  items_summary: {
    pn: string;
    qty: number;
  }[];
  /** 該訂單所屬 supplier 的 system_setting.hide_amount_on_pending；待確認且為 true 時前端隱藏金額。 */
  hide_amount_on_pending?: boolean;
  /**
   * 金錢退款狀態（輕量旗標，僅供列表畫小標）。
   * 列表不該為了畫一枚標就把整包退款資料拉下來；明細在詳情頁的 refund_orders。
   * 舊版 API 沒有這個欄位，故為選填，未帶時視為 none。
   */
  refund_state?: "none" | "running" | "done";
}

export interface OrderGas {
  orderGasId: number;
  numbersOfCylinder: number;
  gasPriceInfo: GasPriceInfo;
  cisGasPriceInfo: unknown;
  customerDeliveryInfo: CustomerDeliveryInfo;
}

export interface GasPriceInfo {
  gpId: number;
  price: number;
  gasCylinderInfo: GasCylinderInfo;
}

export interface GasCylinderInfo {
  gasId: number;
  kilogram: number;
  gasType: string;
}

export interface CustomerDeliveryInfo {
  customerDeliveryId: number;
  deliveryLocation: string;
  usageName: string;
  floor: number;
  isElevator: boolean;
}

export interface OrderCommodity {
  orderCommodityId: number;
  numbersOfCommodity: number;
  commodityPriceInfo: CommodityPriceInfo;
}

export interface CommodityPriceInfo {
  commodityPriceId: number;
  price: number;
  commodityInfo: CommodityInfo;
}

export interface CommodityInfo {
  commodityId: number;
  commodityName: string;
  commodityIntroduction: string | null;
  commodityType: string;
}

export interface OrderUsageFee {
  orderUsageFeeId: number;
  numberOfRecords: number;
  money: number;
}

export interface AddressBindingInfo {
  addressBindingId: number;
  addressId: number;
  customerAddressInfo: CustomerAddressInfo;
  address: string;
}

export interface CustomerAddressInfo {
  customerAddressId: number;
  customerId: number;
  city: string;
  siteId: string;
  village: string | null;
  neighborhood: string | null;
  road: string;
  section: string;
  lane: string | null;
  addressNumber: string;
  floor: string;
  extra: string | null;
  latitude: number;
  longitude: number;
}

export interface CourierInfo {
  courierId: string;
  courierName: string;
}
